<template>
  <div
    class="h-full w-full max-h-full overflow-hidden bg-surface-0 dark:bg-surface-900"
  >
    <div v-if="!isMd" class="h-full w-full flex flex-col relative">
      <div
        v-if="mobileView === 'settings'"
        class="flex-1 overflow-auto p-4 pt-6"
      >
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold">Plot Settings</h2>
          <Button
            icon="pi pi-times"
            rounded
            severity="secondary"
            variant="text"
            @click="mobileView = 'chart'"
          />
        </div>

        <PlotParameters
          v-model:plotType="plotType"
          v-model:params="plotParams.data"
          :plot-types="plotTypes"
          :plot-types-loading="plotTypesLoading"
          :plot-type-info="plotTypeInfo"
          :plot-loading="plotLoading"
          :query-store-result-cols="queryStore.resultCols"
          :is-mobile="true"
          @run="runPlotQuery"
          @change="handlePlotTypeChange"
        />
      </div>

      <div v-else class="flex-1 h-full w-full relative">
        <PlotDisplay
          :result-key="resultKey"
          :plot-loading="plotLoading"
          :iframe-html="iframeHtml"
        />

        <Button
          icon="pi pi-sliders-h"
          rounded
          outlined
          class="!absolute top-2 left-2 shadow-xl z-50 !w-8 !h-8 opacity-80 hover:opacity-100"
          @click="mobileView = 'settings'"
        />
      </div>
    </div>

    <Splitter v-else class="h-full w-full border-none" :gutter-size="2">
      <SplitterPanel
        class="max-h-full max-w-[19rem] w-[19rem] border-r border-surface-200 dark:border-surface-700"
      >
        <div class="overflow-auto h-full p-4">
          <PlotParameters
            v-model:plotType="plotType"
            v-model:params="plotParams.data"
            :plot-types="plotTypes"
            :plot-types-loading="plotTypesLoading"
            :plot-type-info="plotTypeInfo"
            :plot-loading="plotLoading"
            :query-store-result-cols="queryStore.resultCols"
            :is-mobile="false"
            @run="runPlotQuery"
            @change="handlePlotTypeChange"
          />
        </div>
      </SplitterPanel>

      <SplitterPanel :min-size="10" :size="80" class="max-h-full p-2">
        <PlotDisplay
          :result-key="resultKey"
          :plot-loading="plotLoading"
          :iframe-html="iframeHtml"
        />
      </SplitterPanel>
    </Splitter>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, toRaw } from "vue";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import Button from "primevue/button";

import axiosRepo from "@/repositories/AxiosRepository";
import { useQueryStore } from "@/stores/queryStore.js";
import PlotParameters from "./PlotParameters.vue";
import PlotDisplay from "./PlotDisplay.vue";

const queryStore = useQueryStore();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMd = breakpoints.greaterOrEqual("md");
const mobileView = ref("settings");

const plotTypesLoading = ref(false);
const plotLoading = ref(false);
const plotTypes = ref([]);
const plotType = ref();
const plotTypeInfo = ref();
const plotParams = reactive({ data: {} });
const iframeHtml = ref("");
const resultKey = ref(0);

async function getPlotTypes() {
  plotTypesLoading.value = true;
  let res = await axiosRepo.get_plots();
  if (res.status === 200) plotTypes.value = res.data;
  plotTypesLoading.value = false;
}

async function handlePlotTypeChange() {
  plotParams.data = {};
  if (!plotType.value) return;
  let res = await axiosRepo.post(plotType.value["info_path"]);
  if (res.status === 200) {
    plotTypeInfo.value = res.data;
    Object.keys(res.data.args).forEach((name) => {
      plotParams.data[name] = res.data.args[name].default;
    });
  }
}

async function runPlotQuery() {
  if (!isMd.value) mobileView.value = "chart";
  plotLoading.value = true;
  const bodyParams = {};
  const queryParams = { destination_table_path: queryStore.results.table_path };

  Object.entries(plotParams.data).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) return;
    if (typeof value === "object" && !Array.isArray(value))
      bodyParams[key] = value;
    else queryParams[key] = value;
  });

  queryParams["dark_mode"] =
    document.documentElement.classList.contains("dark");
  const queryString = new URLSearchParams(toRaw(queryParams)).toString();
  const requestUrl = plotType.value["path"] + "?" + queryString;

  let res = await axiosRepo.post(requestUrl, bodyParams);
  if (res.status === 200) {
    const iframe_content = `<html lang="en" style="${
      queryParams["dark_mode"] ? "color-scheme: dark;" : ""
    }">
        <body style="margin: 0; overflow: hidden;">${res.data}</body></html>`;
    iframeHtml.value = "data:text/html;base64," + btoa(iframe_content);
    resultKey.value++;
  }
  plotLoading.value = false;
}

onMounted(getPlotTypes);
</script>
